/**
 * DP6 · MobileDrawer — universal bottom-sheet pattern for mobile.
 *
 * Renders a bottom-sheet drawer on mobile (<md). On desktop it behaves as a
 * side-panel or modal, depending on the `desktopVariant` prop.
 *
 * Animations:
 *   - Open:  translateY(100%) → translateY(0), 280ms ease-drawer
 *   - Close: translateY(0) → translateY(100%), 220ms ease-out
 *   - Backdrop: opacity 0 → 0.5, 200ms ease-out
 *
 * Swipe-to-dismiss: drag handle at top; dragging down > 30% of sheet
 * height triggers dismiss.
 *
 * Closes on:
 *   - ESC key
 *   - Backdrop tap
 *   - Swipe down past threshold
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/cn';
import { useFocusTrap } from '@/hooks/useFocusTrap';

interface MobileDrawerProps {
  /** Whether the drawer is visible */
  open: boolean;
  /** Called when the drawer requests closing */
  onClose: () => void;
  /** Drawer content */
  children: ReactNode;
  /** Optional header title */
  title?: string;
  /** Extra classes on the sheet panel */
  className?: string;
  /** aria-label for the dialog (defaults to title if provided) */
  'aria-label'?: string;
}

const EASE_DRAWER = 'cubic-bezier(0.32, 0.72, 0, 1)';
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

export function MobileDrawer({
  open,
  onClose,
  children,
  title,
  className,
  'aria-label': ariaLabel,
}: MobileDrawerProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  // Drag state
  const dragStartY = useRef(0);
  const currentDragY = useRef(0);
  const isDragging = useRef(false);

  // Manage mount/unmount with animation
  useEffect(() => {
    if (open) {
      setVisible(true);
      setAnimating(false);
    } else if (visible) {
      setAnimating(true);
      const t = setTimeout(() => {
        setVisible(false);
        setAnimating(false);
      }, 240);
      return () => clearTimeout(t);
    }
  }, [open, visible]);

  // Trap Tab focus inside the sheet and restore focus to the trigger when
  // it closes. Gated on `open && visible` so the effect re-runs once the
  // sheet is actually in the DOM (visible flips true a render after open),
  // ensuring sheetRef is populated when focus moves in.
  useFocusTrap(sheetRef, open && visible);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Swipe-to-dismiss handlers
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    dragStartY.current = e.touches[0]?.clientY ?? 0;
    currentDragY.current = 0;
    isDragging.current = true;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const dy = (e.touches[0]?.clientY ?? 0) - dragStartY.current;
    if (dy < 0) return; // only downward
    currentDragY.current = dy;
    if (sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${dy}px)`;
      sheetRef.current.style.transition = 'none';
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    isDragging.current = false;
    const el = sheetRef.current;
    if (!el) return;
    const sheetH = el.offsetHeight;
    const dy = currentDragY.current;
    if (dy > sheetH * 0.3) {
      // Dismiss
      el.style.transition = `transform 200ms ${EASE_OUT}`;
      el.style.transform = 'translateY(100%)';
      setTimeout(onClose, 210);
    } else {
      // Snap back
      el.style.transition = `transform 280ms ${EASE_DRAWER}`;
      el.style.transform = 'translateY(0)';
    }
    currentDragY.current = 0;
  }, [onClose]);

  if (!visible) return null;

  const isClosing = animating && !open;
  const label = ariaLabel ?? title ?? 'Drawer';

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-[55] bg-black/50 backdrop-blur-[2px]',
          'transition-opacity duration-200 ease-out',
          isClosing ? 'opacity-0' : 'opacity-100',
        )}
        onClick={onClose}
        aria-hidden
        data-testid="mobile-drawer-backdrop"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-testid="mobile-drawer"
        style={{
          transform: isClosing ? 'translateY(100%)' : 'translateY(0)',
          transition: isClosing
            ? `transform 220ms ${EASE_OUT}`
            : `transform 280ms ${EASE_DRAWER}`,
        }}
        className={cn(
          'fixed inset-x-0 bottom-0 z-[60]',
          'max-h-[90dvh] overflow-y-auto',
          'rounded-t-2xl border-t border-line-strong bg-surface-2',
          'pb-[env(safe-area-inset-bottom)]',
          'shadow-[0_-4px_24px_rgba(0,0,0,0.12)]',
          className,
        )}
      >
        {/* Drag handle */}
        <div
          className="flex touch-none select-none flex-col items-center pb-2 pt-3"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          aria-hidden
          data-testid="mobile-drawer-handle"
        >
          <div className="h-1 w-8 rounded-full bg-line-strong" />
        </div>

        {/* Header row */}
        {title && (
          <div className="flex items-center justify-between border-b border-line px-5 py-3">
            <h2 className="text-[15px] font-semibold tracking-tight text-ink">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="pressable flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl text-ink-muted hover:bg-surface-3 hover:text-ink"
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Content */}
        <div className="px-5 py-4">{children}</div>
      </div>
    </>
  );
}
